import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, usePermissions } from '@hooks';
import Drawer from '@components/Drawer';
import Card from '@components/Card';
import { Typography } from '@components/Typography';
import { AccIntegrationName } from '@constants/integrations';
import { isIntegrationConnected, isIntegrationPending } from '@helpers/integrations';
import { handleApiError } from '@helpers/api';
import { alertActions, ifoodActions, accountActions, locationsProfilesActions } from '@actions';
import { ifoodIntegrationService } from '@services';
import { RoutePath } from '@constants/routes';
import { BRAND_TEAL } from '@constants/colors';

const IFoodIntegration = ({ onClose, onConnect, isOpen, selectedCompany, setSelectedCompany }) => {
    const { t } = useTranslation();
    const [merchant, setMerchant] = useState({});

    const account = useSelector(state => state.account.account);
    const { hasFullSettings } = usePermissions();
    const canManageIntegrations = hasFullSettings();

    const MERCHANTS = useSelector(state => state.ifood.data);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const IFOOD_CONNECTED = isIntegrationConnected(account, AccIntegrationName.iFood);
    const IFOOD_PENDING = isIntegrationPending(account, AccIntegrationName.iFood);

    useEffect(() => {
        if (IFOOD_CONNECTED && !IFOOD_PENDING) {
            dispatch(ifoodActions.get());
        }
    }, [dispatch, IFOOD_CONNECTED, IFOOD_PENDING]);

    useEffect(() => {
        if (MERCHANTS.length) {
            const availableMerchant = MERCHANTS.find(m => !m.connected);
            if (availableMerchant) {
                setMerchant(availableMerchant);
            }
        }
    }, [MERCHANTS]);

    const goToIntegrations = () => navigate(RoutePath.AccountSettingsIntegrations);

    const onConnectIFood = async () => {
        try {
            onClose();
            const updatedCompany = await ifoodIntegrationService.editProfile(selectedCompany?.companyId, {
                locationId: selectedCompany?.companyId,
                restaurantId: merchant.id || null,
                name: merchant.name || null,
            });
            await dispatch(accountActions.get());
            await dispatch(locationsProfilesActions.get(selectedCompany.companyId));
            dispatch(ifoodActions.reset());
            setSelectedCompany(updatedCompany);
            dispatch(alertActions.success(t('SettingsCompanies.connectIntegrationMsg')));
        } catch (error) {
            handleApiError(error);
        } finally {
            onConnect();
        }
    };

    const getPrimaryActionProps = () => {
        if (IFOOD_PENDING) {
            return undefined;
        }
        if (!IFOOD_CONNECTED && canManageIntegrations) {
            return {
                primaryAction: goToIntegrations,
                primaryText: t('SettingsCompanies.goToIntegrations'),
                primaryDisabled: false,
            };
        } else if (IFOOD_CONNECTED && canManageIntegrations) {
            return {
                primaryAction: onConnectIFood,
                primaryText: t('buttons.connect'),
                primaryDisabled: !merchant.id,
            };
        } else if (!IFOOD_CONNECTED && !canManageIntegrations) {
            return {
                primaryAction: goToIntegrations,
                primaryText: t('SettingsCompanies.goToIntegrations'),
                primaryDisabled: true,
            };
        }
    };

    return (
        <Drawer
            aboveModal
            anchor="right"
            open={isOpen}
            onClose={onClose}
            title={
                IFOOD_PENDING
                    ? t('SettingsCompanies.iFoodPendingTitle')
                    : IFOOD_CONNECTED
                      ? t('SettingsCompanies.connectLocation')
                      : t('SettingsCompanies.connectDrawerNotConnected')
            }
            subtitle={
                IFOOD_PENDING
                    ? t('SettingsCompanies.iFoodPendingDescription')
                    : IFOOD_CONNECTED
                      ? t('SettingsCompanies.connectDrawerDescription')
                      : t('SettingsCompanies.connectDrawerNotConnectedDescription')
            }
            {...getPrimaryActionProps()}
        >
            <div style={{ display: 'grid', gridGap: '8px' }}>
                {IFOOD_CONNECTED &&
                    !IFOOD_PENDING &&
                    merchant &&
                    MERCHANTS.map(m => (
                        <Card
                            key={m.id}
                            type="radio"
                            selected={merchant.id === m.id && !m.connected}
                            title={m.name}
                            value={m.id}
                            setValue={() => setMerchant(m)}
                            disabled={m.connected}
                        >
                            {m.connected && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#fff',
                                        backgroundColor: BRAND_TEAL,
                                        padding: '2px 6px',
                                        borderRadius: '18px',
                                        width: 'fit-content',
                                    }}
                                >
                                    {m.connectedLocationName}
                                </Typography>
                            )}
                        </Card>
                    ))}
            </div>
        </Drawer>
    );
};

export default IFoodIntegration;
